"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { telegramAuth } from "@/features/auth/api"
import { getTelegramInitData, expandTelegramApp } from "@/shared/lib/telegram"
import { api } from "@/shared/lib/axios"

export default function HomePage() {
  const router = useRouter()
  const [status, setStatus] = useState<"loading" | "choice" | "error">("loading")
  const [telegramInit, setTelegramInit] = useState<string | null>(null)
  const [theme, setTheme] = useState<"light" | "dark">("light")

  useEffect(() => {
    expandTelegramApp()
    const initData = getTelegramInitData()
    if (!initData) {
      console.warn("Telegram initData не получен")
      router.replace("/login")
      return
    }

    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp
      const isDark =
        tg.colorScheme === "dark" ||
        (tg.colorScheme === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches)
      setTheme(isDark ? "dark" : "light")
      tg.onEvent("themeChanged", () => {
        const newIsDark =
          tg.colorScheme === "dark" ||
          (tg.colorScheme === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches)
        setTheme(newIsDark ? "dark" : "light")
      })
    }

    let isMounted = true
    const authenticate = async () => {
      try {
        const authData = await telegramAuth(initData)
        sessionStorage.setItem("access_token", authData.access_token)
        setTelegramInit(initData)

        if (authData.needs_registration) {
          if (isMounted) setStatus("choice")
          return
        }

        const { data: user } = await api.get("/users/me")
        if (user?.is_active) {
          if (isMounted) router.replace("/home")
        } else {
          if (isMounted) router.replace("/activation-pending")
        }
      } catch (err) {
        console.error("Ошибка авторизации через Telegram:", err)
        setStatus("error")
      }
    }

    authenticate()
    return () => { isMounted = false }
  }, [router])

  const bg = theme === "dark" ? "bg-gray-950" : "bg-gray-50"
  const cardBg = theme === "dark" ? "bg-gray-900" : "bg-white"
  const text = theme === "dark" ? "text-gray-100" : "text-gray-900"
  const textSecondary = theme === "dark" ? "text-gray-400" : "text-gray-600"

  if (status === "error") {
    return (
      <div className={`min-h-screen ${bg} flex flex-col items-center justify-center p-6`}>
        <div className="text-red-500 text-6xl mb-4">!</div>
        <h2 className={`text-2xl font-semibold mb-2 ${text}`}>Не удалось авторизоваться</h2>
        <p className={`text-center mb-6 ${textSecondary}`}>Пожалуйста, попробуйте открыть приложение заново</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-500 transition"
        >
          Попробовать снова
        </button>
      </div>
    )
  }

  if (status === "choice") {
    return (
      <div className={`min-h-screen ${bg} flex flex-col items-center justify-center px-4 py-6`}>
        <div className={`${cardBg} w-full max-w-md p-6 rounded-2xl shadow-md border ${theme === "dark" ? "border-gray-800" : "border-gray-200"}`}>
          <h1 className={`text-2xl font-bold mb-4 text-center ${text}`}>Добро пожаловать!</h1>
          <p className={`text-center mb-6 ${textSecondary}`}>Вы вошли через Telegram, но ещё не зарегистрированы.</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => router.push("/register?telegramInit=" + encodeURIComponent(telegramInit || ""))}
              className="w-full py-3 px-4 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-500 transition"
            >
              Зарегистрироваться
            </button>
            <button
              onClick={() => router.push("/bind-telegram?telegramInit=" + encodeURIComponent(telegramInit || ""))}
              className="w-full py-3 px-4 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-500 transition"
            >
              Привязать Telegram к существующему аккаунту
            </button>
          </div>
        </div>
      </div>
    )
  }

  // loading
  return (
    <div className={`min-h-screen ${bg} flex flex-col items-center justify-center`}>
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-opacity-50"></div>
      <p className={`mt-6 text-lg font-medium ${text}`}>Авторизация...</p>
      <p className={`mt-2 text-sm ${textSecondary}`}>Пожалуйста, подождите</p>
    </div>
  )
}